import React, { useEffect, useState } from 'react';
import {
    asyncDeleteQuestion,
    asyncGetAnswersByQuestion,
    asyncGetQuestion,
    asyncGetQuestions,
    asyncUpdatePositions,
} from '../../../../services/survey.service';
import { Chip, IconButton, Tooltip } from '@mui/material';
import ModalQuestion, { typesSupported } from './modalQuestion';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from 'sweetalert2';
import { MasterMessage } from '../../../../masters/masterMessages';
import { SwalCustomClass } from '../../../components/general/toastSwal/swalCustomClass';
import EditIcon from '@mui/icons-material/Edit';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import ModalRange from './modalRange';
import ModalAnswers from './modalAnswers';
import ModalValidationReference from './modalValidationReference';
import DraggableListView from '../../../components/views/draggableListView';

type Props = {
    idSurvey?: any;
    saveQuestions?: any;
    isTextTypeQuestion?: boolean;
};

const SurveyQuestions = ({
    idSurvey,
    saveQuestions,
    isTextTypeQuestion,
}: Props) => {
    const { questions, changePosition }: any = MasterMessage.surveys;
    const [rowQuestion, setRowQuestion] = useState<any>();
    const [rowRange, setRowRange] = useState<any>();
    const [rows, setRows] = useState<any>(undefined);
    const [page, setPage] = useState<number>(0);
    const [openAddQuestion, setOpenAddQuestion] = useState<boolean>(false);
    const [openRange, setOpenRange] = useState<boolean>(false);
    const [openAnswers, setOpenAnswers] = useState<boolean>(false);
    const [saveLoad, setSaveLoad] = useState<boolean>(false);
    const [idQuestion, setIdQuestion] = useState<string>('');
    const [idQuestionRange, setIdQuestionRange] = useState<string>('');
    const [idAnswer, setIdAnswer] = useState<string>('');
    const [loadingEdit, setLoadingEdit] = useState<boolean>(true);

    const [openValidationReference, setOpenValidationReference] =
        useState<boolean>(false);
    const [idQuestionValidationReference, setIdQuestionValidationReference] =
        useState<string>('');
    const [rowValidationReference, setRowValidationReference] = useState<any>();
    const [idAnswerValidationReference, setIdAnswerValidationReference] =
        useState<string>('');

    useEffect(() => {
        if (saveLoad && page !== 0) {
            setPage(0);
            setSaveLoad(false);
            return;
        } else if (saveLoad) setSaveLoad(false);

        if (saveLoad || rows === undefined) {
            if (idSurvey) {
                asyncGetQuestions(page, 5000, idSurvey, true).then(
                    (body: any) => {
                        if (body.items !== undefined) {
                            setRows(body.items.items);
                        } else {
                            SwalCustomClass.fire({
                                text: questions[body.message],
                                icon: 'error',
                                iconColor: '#1d1d1d',
                            });
                        }
                    }
                );
            }
        }

        if (idQuestion && !idAnswer) {
            setLoadingEdit(true);
            asyncGetQuestion(idQuestion)
                .then((body: any) => {
                    if (body.items !== undefined) {
                        setRowQuestion(body.items);
                    }
                })
                .finally(() => {
                    window.setTimeout(() => {
                        setLoadingEdit(false);
                    }, 1200);
                });
        }

        if (idQuestionRange) {
            setLoadingEdit(true);
            asyncGetAnswersByQuestion(idQuestionRange)
                .then((body: any) => {
                    if (body.items !== undefined) {
                        setRowRange(body.items.items[0]);
                    }
                })
                .finally(() => {
                    window.setTimeout(() => {
                        setLoadingEdit(false);
                    }, 1200);
                });
        }

        if (idQuestionValidationReference) {
            setLoadingEdit(true);
            asyncGetAnswersByQuestion(idQuestionValidationReference)
                .then((body: any) => {
                    if (body.items !== undefined) {
                        const items = body.items.items ?? [];
                        if (idAnswerValidationReference) {
                            const found = items.find(
                                (a: any) => a.id === idAnswerValidationReference
                            );
                            setRowValidationReference(
                                found ?? items[0] ?? undefined
                            );
                        } else {
                            setRowValidationReference(items[0]);
                        }
                    }
                })
                .finally(() => {
                    window.setTimeout(() => {
                        setLoadingEdit(false);
                    }, 1200);
                });
        }

        saveQuestions(() => onOpenAddQuestion);
    }, [
        page,
        saveLoad,
        idQuestion,
        idAnswer,
        idQuestionRange,
        idQuestionValidationReference,
        idAnswerValidationReference,
        idSurvey,
    ]);

    const onOpenAddQuestion = () => {
        setOpenAddQuestion(true);
    };

    const deleteQuestion = (rowId: string) => {
        Swal.fire({
            title: questions.titleInactive,
            text: questions.messageInactive,
            icon: 'error',
            iconColor: '#1d1d1d',
            showDenyButton: true,
            denyButtonText: 'NO',
            denyButtonColor: '#1d1d1d',
            confirmButtonText: 'SI',
            confirmButtonColor: '#1d1d1d',
            customClass: 'swal-wide-class',
        }).then((response) => {
            if (response.isConfirmed) {
                asyncDeleteQuestion(rowId, idSurvey).then((body: any) => {
                    if (body.items !== undefined) {
                        SwalCustomClass.fire({
                            text: questions.messageInactiveOk,
                            icon: 'success',
                            iconColor: '#1d1d1d',
                        });
                        asyncGetQuestions(page, 5000, idSurvey, true).then(
                            (body: any) => {
                                if (body.items !== undefined) {
                                    setRows(body.items.items);
                                }
                            }
                        );
                    } else {
                        SwalCustomClass.fire({
                            text: questions[body.message],
                            icon: 'error',
                            iconColor: '#1d1d1d',
                        });
                    }
                });
            }
        });
    };

    const onEditClickQuestion = (rowId: string) => {
        setIdQuestion(rowId);
        setOpenAddQuestion(true);
    };

    const onCloseClickQuestion = (value: boolean) => {
        setIdQuestion('');
        setRowQuestion(undefined);
        setOpenAddQuestion(value);
    };

    const onOpenClickRange = (item: any) => {
        setIdQuestionRange(item.id);
        setIdAnswer(
            item?.row?.answers.length > 0 ? item?.row?.answers[0].id : ''
        );
        setOpenRange(true);
    };

    const onCloseClickRange = (value: boolean) => {
        setOpenRange(value);
        setIdQuestionRange('');
        setRowRange(undefined);
        setIdAnswer('');
    };

    const onOpenClickValidationReference = (item: any) => {
        setIdQuestionValidationReference(item.id);
        setIdAnswerValidationReference(
            item?.row?.answers?.length > 0 ? item?.row?.answers[0].id : ''
        );
        setRowValidationReference(
            item?.row?.answers?.length > 0 ? item?.row?.answers[0] : undefined
        );
        setOpenValidationReference(true);
    };

    const onCloseClickValidationReference = (value: boolean) => {
        setOpenValidationReference(value);
        setIdQuestionValidationReference('');
        setRowValidationReference(undefined);
        setIdAnswerValidationReference('');
    };

    const onOpenClickAnswers = (item: any) => {
        setOpenAnswers(true);
        setIdQuestion(item.id);
    };

    const onCloseClickAnswers = (value: boolean) => {
        setOpenAnswers(value);
        setIdQuestion('');
    };

    const columns = [
        {
            field: 'description',
            headerName: 'Descripción',
            minWidth: '30%',
            flex: 2,
        },
        {
            field: 'type',
            headerName: 'Tipo Pregunta',
            minWidth: '12%',
            flex: 0.8,
        },
        {
            field: 'subtype',
            headerName: 'Subtipo Pregunta',
            minWidth: '12%',
            flex: 0.8,
        },
        {
            field: 'answers',
            headerName: 'Respuesta(s)',
            minWidth: '8%',
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row: any) =>
                typesSupported[row.row.type] && (
                    <Tooltip
                        title={
                            row.row.type === 'linear' ? 'Rango' : 'Respuesta(s)'
                        }
                    >
                        <IconButton
                            size="small"
                            onClick={() =>
                                row.row.type === 'linear'
                                    ? onOpenClickRange(row)
                                    : row.row.type === 'validation-reference'
                                        ? onOpenClickValidationReference(row)
                                        : onOpenClickAnswers(row)
                            }
                        >
                            {row.row.type === 'linear' ? (
                                <LinearScaleIcon
                                    type="submit"
                                    color="primary"
                                />
                            ) : row.row.type === 'validation-reference' ? (
                                <AppRegistrationIcon
                                    type="submit"
                                    color="primary"
                                />
                            ) : (
                                <QuestionAnswerIcon
                                    type="submit"
                                    color="primary"
                                />
                            )}
                        </IconButton>
                    </Tooltip>
                ),
        },
        {
            field: 'why',
            headerName: 'Porque',
            minWidth: '10%',
            flex: 0.5,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row: any) => {
                if (row.row.why === true) {
                    return (
                        <Chip
                            label="ACTIVO"
                            sx={{ ml: 1, fontSize: 11.5 }}
                            color="success"
                        />
                    );
                } else {
                    return (
                        <Chip
                            label="INACTIVO"
                            sx={{ ml: 1, fontSize: 11.5 }}
                            color="error"
                        />
                    );
                }
            },
        },
        {
            field: 'dependency',
            headerName: 'Dependencia',
            minWidth: '10%',
            flex: 0.5,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row: any) => {
                if (
                    row.row.dependency === true &&
                    row.row.position >
                        row.row?.questionDependency[0]?.questionDependency
                            ?.position
                ) {
                    return (
                        <Chip
                            label="ACTIVO"
                            sx={{ ml: 1, fontSize: 11.5 }}
                            color="success"
                        />
                    );
                } else {
                    return (
                        <Chip
                            label="INACTIVO"
                            sx={{ ml: 1, fontSize: 11.5 }}
                            color="error"
                        />
                    );
                }
            },
        },
        {
            field: 'status',
            headerName: 'Estado',
            minWidth: '10%',
            flex: 0.5,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row: any) => {
                switch (row.row.status) {
                    case 'active':
                        return (
                            <Chip
                                label="ACTIVO"
                                sx={{ ml: 1, fontSize: 11.5 }}
                                color="success"
                            />
                        );
                    case 'inactive':
                        return (
                            <Chip
                                label="INACTIVO"
                                sx={{ ml: 1, fontSize: 11.5 }}
                                color="error"
                            />
                        );
                    default:
                        return (
                            <Chip
                                label="EN PROCESO"
                                sx={{ ml: 1, fontSize: 11.5 }}
                                color="info"
                            />
                        );
                }
            },
        },
        {
            field: 'actions',
            headerName: 'Acciones',
            minWidth: '8%',
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row: any) => (
                <>
                    <Tooltip title="Editar">
                        <IconButton
                            size="small"
                            onClick={() => onEditClickQuestion(row.id)}
                        >
                            <EditIcon type="submit" color="primary" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                        <IconButton
                            size="small"
                            onClick={() => deleteQuestion(row.id)}
                            disabled={row.row.status === 'inactive'}
                        >
                            <DeleteIcon
                                type="submit"
                                color={
                                    row.row.status === 'inactive'
                                        ? 'disabled'
                                        : 'primary'
                                }
                            />
                        </IconButton>
                    </Tooltip>
                </>
            ),
        },
    ];

    const onConfirmChangeItems = (items: any[]) => {
        setRows(items);
        asyncUpdatePositions(
            idSurvey,
            items.map((item: any) => item.id)
        );
        SwalCustomClass.fire({
            text: changePosition.messageInactiveOk,
            icon: 'success',
            iconColor: '#1d1d1d',
        });
    };

    const onChangeItems = (items: any[]) => {
        Swal.fire({
            title: changePosition.titleInactive,
            text: changePosition.messageInactive,
            icon: 'question',
            iconColor: '#1d1d1d',
            showDenyButton: true,
            denyButtonText: 'NO',
            denyButtonColor: '#1d1d1d',
            confirmButtonText: 'SI',
            confirmButtonColor: '#1d1d1d',
            customClass: 'swal-wide-class',
        }).then((response) => {
            if (response.isConfirmed) {
                onConfirmChangeItems(items);
            }
        });
    };

    return (
        <>
            <DraggableListView
                columns={columns}
                rows={rows}
                onChangeItems={onChangeItems}
                deferPositionUpdate
            />
            {openAddQuestion && (
                <ModalQuestion
                    open={openAddQuestion}
                    onClose={(value: boolean) => onCloseClickQuestion(value)}
                    saveLoad={(value: boolean) => setSaveLoad(value)}
                    idSurvey={idSurvey}
                    idQuestion={idQuestion}
                    question={rowQuestion}
                    loading={idQuestion ? loadingEdit : false}
                    questionsDependency={rows}
                    isTextTypeQuestion={isTextTypeQuestion}
                />
            )}
            {openRange && (
                <ModalRange
                    open={openRange}
                    onClose={(value: boolean) => onCloseClickRange(value)}
                    saveLoad={(value: boolean) => setSaveLoad(value)}
                    idSurvey={idSurvey}
                    idQuestion={idQuestionRange}
                    idAnswer={idAnswer}
                    answer={rowRange}
                    loading={idAnswer ? loadingEdit : false}
                />
            )}
            {openValidationReference && (
                <ModalValidationReference
                    open={openValidationReference}
                    onClose={(value: boolean) =>
                        onCloseClickValidationReference(value)
                    }
                    saveLoad={(value: boolean) => setSaveLoad(value)}
                    idSurvey={idSurvey}
                    idQuestion={idQuestionValidationReference}
                    idAnswer={idAnswerValidationReference}
                    answer={rowValidationReference}
                    loading={idAnswerValidationReference ? loadingEdit : false}
                />
            )}
            {openAnswers && (
                <ModalAnswers
                    open={openAnswers}
                    onClose={(value: boolean) => onCloseClickAnswers(value)}
                    saveLoad={(value: boolean) => setSaveLoad(value)}
                    idSurvey={idSurvey}
                    idQuestion={idQuestion}
                    type={rowQuestion?.type}
                />
            )}
        </>
    );
};

export default SurveyQuestions;
